import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  RefreshCw, Loader2, Wallet, ShoppingBag, Undo2, Receipt,
  Settings as SettingsIcon, Truck, Banknote, FileText, BarChart3, Download, Package,
} from 'lucide-react';
import toast from 'react-hot-toast';
import AdminLayout from '../../components/Admin/Layout/AdminLayout';
import MarketplaceSelector from '../../components/shared/MarketplaceSelector';
import settlementReportService from '../../services/settlementReportService';
import { useSeller } from '../../context/SellerContext';
import SettlementOverview from '../../components/Admin/Settlement/SettlementOverview';
import SettlementOrderPnl from '../../components/Admin/Settlement/SettlementOrderPnl';
import SettlementSkuPerformance from '../../components/Admin/Settlement/SettlementSkuPerformance';
import DateRangePicker, { defaultRange } from '../../components/Admin/Settlement/DateRangePicker';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmtMoney = (val) => {
  if (val == null || val === '') return '—';
  const n = Number(val);
  if (Number.isNaN(n)) return '—';
  const sign = n < 0 ? '-' : '';
  return `${sign}₹${Math.abs(n).toLocaleString('en-IN', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
};

const fmtDateTime = (s) => {
  if (!s) return '—';
  const d = new Date(s);
  return d.toLocaleString('en-IN', { day: 'numeric', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit', hour12: true });
};

// Main tabs
const MAIN_TABS = [
  { key: 'overview', label: 'Overview',          icon: BarChart3 },
  { key: 'orderPnl', label: 'Per-Order P&L',     icon: ShoppingBag },
  { key: 'sku',      label: 'SKU Performance',   icon: Package },
  { key: 'raw',      label: 'Raw Transactions',  icon: FileText },
];

// Raw-tab transaction-type filter pills (V2 enum values)
const RAW_FILTERS = [
  { key: 'all',         label: 'All',                icon: FileText,      type: null },
  { key: 'order',       label: 'Orders',             icon: ShoppingBag,   type: 'Order' },
  { key: 'refund',      label: 'Refunds',            icon: Undo2,         type: 'Refund' },
  { key: 'fbaRefund',   label: 'FBA Fee Refunds',    icon: Undo2,         type: 'Fulfillment Fee Refund' },
  { key: 'serviceFee',  label: 'Service Fees',       icon: Receipt,       type: 'ServiceFee' },
  { key: 'adjustment',  label: 'Adjustments',        icon: SettingsIcon,  type: 'Adjustment' },
  { key: 'easyShip',    label: 'Easy Ship',          icon: Truck,         type: 'other-transaction' },
  { key: 'transfer',    label: 'Payment Details',    icon: Banknote,      type: 'Transfer' },
];

// ═════════════════════════════════════════════════════════════════════════════

export default function SettlementReportsPage() {
  const { selectedSeller } = useSeller();
  const sellerChannels = useMemo(
    () => (selectedSeller?.channels || []).filter(
      c => c.accountStatus === 'AUTHORIZED' && (c.platform === 'AMAZON' || !c.platform)
    ),
    [selectedSeller]
  );

  const [selectedAccountId, setSelectedAccountId] = useState('');
  useEffect(() => {
    if (!selectedAccountId && sellerChannels.length > 0) {
      setSelectedAccountId(sellerChannels[0].id);
    }
  }, [sellerChannels, selectedAccountId]);

  const [dateRange, setDateRange] = useState(defaultRange());
  const [activeTab, setActiveTab] = useState('overview');
  const [syncing, setSyncing] = useState(false);
  const [settlementsInRange, setSettlementsInRange] = useState([]);

  // Fetch settlements in current range — drives the chip strip below header
  useEffect(() => {
    if (!selectedAccountId || !dateRange.from || !dateRange.to) {
      setSettlementsInRange([]);
      return;
    }
    let cancelled = false;
    settlementReportService.getSettlementsInRange(selectedAccountId, dateRange.from, dateRange.to)
      .then(res => { if (!cancelled) setSettlementsInRange(Array.isArray(res.data) ? res.data : (res || [])); })
      .catch(() => { if (!cancelled) setSettlementsInRange([]); });
    return () => { cancelled = true; };
  }, [selectedAccountId, dateRange.from, dateRange.to]);

  const handleSync = useCallback(async () => {
    if (!selectedAccountId) { toast.error('Select an Amazon account first'); return; }
    setSyncing(true);
    const t = toast.loading('Pulling settlement reports from Amazon…');
    try {
      const res = await settlementReportService.sync(selectedAccountId);
      const data = res.data || res;
      toast.success(
        `${data.created || 0} new, ${data.skipped || 0} already had, ${data.failed || 0} failed`,
        { id: t }
      );
      // Refresh settlements strip
      const r2 = await settlementReportService.getSettlementsInRange(selectedAccountId, dateRange.from, dateRange.to);
      setSettlementsInRange(Array.isArray(r2.data) ? r2.data : (r2 || []));
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Sync failed', { id: t });
    } finally {
      setSyncing(false);
    }
  }, [selectedAccountId, dateRange.from, dateRange.to]);

  const totalDeposit = useMemo(
    () => settlementsInRange.reduce((s, x) => s + (Number(x.depositAmount) || 0), 0),
    [settlementsInRange]
  );

  return (
    <AdminLayout>
      <div className="p-3 sm:p-5 space-y-4">
        {/* ─── Header ──────────────────────────────────────── */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-bold text-slate-900">Reconciliation</h1>
            <p className="text-xs text-slate-400 mt-0.5">
              See exactly where your Amazon money went — by date range
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <MarketplaceSelector value={selectedAccountId} onChange={setSelectedAccountId} />
            <DateRangePicker value={dateRange} onChange={setDateRange} />
            <button
              onClick={handleSync}
              disabled={syncing || !selectedAccountId}
              title={!selectedAccountId ? 'Select an Amazon account first' : ''}
              className="inline-flex items-center px-4 py-2 bg-[#232F3E] text-white rounded-lg text-sm font-medium hover:bg-[#37475A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {syncing ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Wallet size={16} className="mr-2" />}
              {syncing ? 'Syncing…' : 'Sync from Amazon'}
            </button>
          </div>
        </div>

        {/* ─── Settlements-in-range strip ──────────────────── */}
        {settlementsInRange.length > 0 && (
          <div className="bg-white rounded-lg border border-slate-200 px-4 py-2.5 flex items-center gap-3 flex-wrap">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              {settlementsInRange.length} settlement{settlementsInRange.length === 1 ? '' : 's'} in range
            </span>
            <span className="text-xs font-bold text-slate-700">{fmtMoney(totalDeposit)} disbursed</span>
            <span className="text-[10px] text-slate-400">|</span>
            <div className="flex flex-wrap gap-1.5">
              {settlementsInRange.map(s => (
                <span
                  key={s.id}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono bg-slate-50 border border-slate-200 text-slate-600"
                  title={`${s.settlementId} · ${fmtDateTime(s.amazonCreatedAt)}`}
                >
                  …{(s.settlementId || '').slice(-5)} · <strong className="text-slate-800">{fmtMoney(s.depositAmount)}</strong>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* ─── Tab area ────────────────────────────────────── */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="border-b border-slate-200 px-2">
            <div className="flex">
              {MAIN_TABS.map((tab) => {
                const Icon = tab.icon;
                const active = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`inline-flex items-center gap-1.5 px-4 py-3 text-xs font-semibold border-b-2 transition-colors ${
                      active
                        ? 'border-[#f08804] text-[#C45500]'
                        : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    <Icon size={13} /> {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          {activeTab === 'overview' ? (
            <SettlementOverview
              sellerAccountId={selectedAccountId}
              from={dateRange.from}
              to={dateRange.to}
            />
          ) : activeTab === 'orderPnl' ? (
            <SettlementOrderPnl
              sellerAccountId={selectedAccountId}
              from={dateRange.from}
              to={dateRange.to}
              settlements={settlementsInRange}
            />
          ) : activeTab === 'sku' ? (
            <SettlementSkuPerformance
              sellerAccountId={selectedAccountId}
              from={dateRange.from}
              to={dateRange.to}
            />
          ) : (
            <RawTransactions
              sellerAccountId={selectedAccountId}
              from={dateRange.from}
              to={dateRange.to}
            />
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

// ─── Raw Transactions tab ────────────────────────────────────────────────────

function RawTransactions({ sellerAccountId, from, to }) {
  const [activeFilter, setActiveFilter] = useState('all');
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!sellerAccountId || !from || !to) return;
    let cancelled = false;
    const filter = RAW_FILTERS.find(f => f.key === activeFilter) || RAW_FILTERS[0];
    setLoading(true);
    settlementReportService.getTransactionsByRange(sellerAccountId, from, to, filter.type)
      .then((res) => { if (!cancelled) setTransactions(Array.isArray(res.data) ? res.data : (res || [])); })
      .catch(() => { if (!cancelled) setTransactions([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sellerAccountId, from, to, activeFilter]);

  const totalSum = transactions.reduce((acc, r) => acc + (Number(r.totalAmount) || 0), 0);

  const handleExportCsv = () => {
    if (transactions.length === 0) return;
    const headers = [
      'Date', 'Settlement', 'Type', 'Order ID', 'SKU', 'Description', 'Qty',
      'Marketplace', 'Account Type', 'Fulfilment',
      'Ship City', 'Ship State', 'Ship Postal',
      'Product Sales', 'Shipping', 'Gift Wrap', 'Promo Rebates', 'GST Liable',
      'TCS-CGST', 'TCS-SGST', 'TCS-IGST', 'TDS 194-O',
      'Selling Fees', 'FBA Fees', 'Other Tx Fees', 'Other', 'Total',
    ];
    const esc = (v) => {
      const s = v == null ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const num = (v) => (v == null || v === '' ? 0 : Number(v));
    const csvRows = transactions.map((r) => [
      esc(r.transactionDatetime), esc(r.settlementId), esc(r.transactionType),
      esc(r.amazonOrderId), esc(r.sku), esc(r.description), num(r.quantity),
      esc(r.marketplace), esc(r.accountType), esc(r.fulfillment),
      esc(r.shipCity), esc(r.shipState), esc(r.shipPostal),
      num(r.productSales), num(r.shippingCredits), num(r.giftWrapCredits),
      num(r.promotionalRebates), num(r.salesTaxLiableGst),
      num(r.tcsCgst), num(r.tcsSgst), num(r.tcsIgst), num(r.tdsSection194o),
      num(r.sellingFees), num(r.fbaFees), num(r.otherTransactionFees),
      num(r.otherAmount), num(r.totalAmount),
    ].join(','));
    const csv = [headers.join(','), ...csvRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const filterKey = (RAW_FILTERS.find(f => f.key === activeFilter) || {}).key || 'all';
    a.download = `raw-transactions_${filterKey}_${from}_to_${to}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4 sm:p-5 space-y-3">
      <div className="flex flex-wrap items-center gap-1.5">
        {RAW_FILTERS.map(f => {
          const Icon = f.icon;
          const active = activeFilter === f.key;
          return (
            <button
              key={f.key}
              onClick={() => setActiveFilter(f.key)}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold border transition-colors ${
                active
                  ? 'bg-[#FFF4E6] border-[#f08804] text-[#C45500]'
                  : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              <Icon size={11} /> {f.label}
            </button>
          );
        })}

        <div className="flex-1" />

        <button
          onClick={handleExportCsv}
          disabled={transactions.length === 0}
          className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium bg-white border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 disabled:opacity-50"
        >
          <Download size={11} /> Export CSV
        </button>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-8 flex items-center justify-center text-slate-400">
            <Loader2 size={18} className="animate-spin" />
          </div>
        ) : transactions.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">No transactions in this filter.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                <tr>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-left">Order ID</th>
                  <th className="px-3 py-2 text-left">SKU</th>
                  <th className="px-3 py-2 text-right">Qty</th>
                  <th className="px-3 py-2 text-right">Sales</th>
                  <th className="px-3 py-2 text-right">GST</th>
                  <th className="px-3 py-2 text-right">Selling Fees</th>
                  <th className="px-3 py-2 text-right">FBA Fees</th>
                  <th className="px-3 py-2 text-right">Other Fees</th>
                  <th className="px-3 py-2 text-right">TCS</th>
                  <th className="px-3 py-2 text-right">TDS</th>
                  <th className="px-3 py-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {transactions.map(r => {
                  const tcs = (Number(r.tcsCgst) || 0) + (Number(r.tcsSgst) || 0) + (Number(r.tcsIgst) || 0);
                  return (
                    <tr key={r.id} className="hover:bg-slate-50/50">
                      <td className="px-3 py-2 whitespace-nowrap">{fmtDateTime(r.transactionDatetime)}</td>
                      <td className="px-3 py-2">{r.transactionType || '—'}</td>
                      <td className="px-3 py-2 font-mono text-[11px]">{r.amazonOrderId || '—'}</td>
                      <td className="px-3 py-2 font-mono text-[11px]" title={r.description}>{r.sku || '—'}</td>
                      <td className="px-3 py-2 text-right">{r.quantity ?? '—'}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(r.productSales)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(r.salesTaxLiableGst)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(r.sellingFees)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(r.fbaFees)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(r.otherTransactionFees)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(tcs)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(r.tdsSection194o)}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold">{fmtMoney(r.totalAmount)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-slate-50 border-t border-slate-200 font-bold text-slate-700">
                <tr>
                  <td colSpan={12} className="px-3 py-2 text-right text-[10px] uppercase tracking-wider text-slate-500">
                    Total ({transactions.length} rows)
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(totalSum)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
